import { chain } from "@/config";
import { convertToDays } from "@/helpers/number_format_helper";
import { selectEtkByAddress } from "@store/eTokens/selectors";
import { selectPAByAddress } from "@store/premiumsAccounts/selectors";

interface LoadedState<T> {
  state: "LOADED";
  value: T;
}

interface LoadingState {
  state: "LOADING";
  value: undefined;
}

type AsyncState<T> = LoadedState<T> | LoadingState;

interface PremiumAccount {
  address: string;
  name: string;
}

interface EtkWithName {
  name: string;
  address: string;
}

export type RiskParamValue = number | string | null | undefined;

export interface RiskParams {
  moc: AsyncState<RiskParamValue>;
  jrCollRatio: AsyncState<RiskParamValue>;
  srCollRatio: AsyncState<RiskParamValue>;
  ensuroFee: AsyncState<RiskParamValue>;
  jrRoc: AsyncState<RiskParamValue>;
  srRoc: AsyncState<RiskParamValue>;
  maxPayout: AsyncState<RiskParamValue>;
  maxDuration: AsyncState<RiskParamValue>;
  avgPayoutPerPolicy: AsyncState<RiskParamValue>;
  avgPolicyDuration: AsyncState<RiskParamValue>;
  activeExposure: AsyncState<RiskParamValue>;
}

interface VisibilityObject {
  custom_data?: {
    tags?: string[];
  };
}

export function getUtilizationRate(totalSupply: AsyncState<number>, scr: AsyncState<number>): string | undefined {
  if (totalSupply.state === "LOADED" && scr.state === "LOADED") {
    return totalSupply.value === 0 ? "0" : ((scr.value / totalSupply.value) * 100).toFixed(1);
  }
  return undefined;
}

export function calcOnLoad<T>(fn: (...args: any[]) => T, ...variables: AsyncState<any>[]): AsyncState<T> {
  if (variables.every((variable) => variable.state === "LOADED")) {
    const values = variables.map((variable) => variable.value);
    const result = fn(...values);
    return { state: "LOADED", value: result !== undefined ? result : (0 as T) };
  }
  return { state: "LOADING", value: undefined };
}

export function truncateLabel(str: string | undefined | null, maxLength = 20): string {
  const s = typeof str === "string" ? str : "";
  return s.length > maxLength ? s.slice(0, maxLength - 3) + "..." : s;
}

export function etherscanLink(address: string): string {
  return chain.scan + "address/" + address;
}

export function getAllPremiumsAccountsByEtk(
  etkPAs: string[] | null | undefined,
  allPAs: Record<string, string> | null | undefined
): PremiumAccount[] {
  let paInEtk: PremiumAccount[] = [];
  if (etkPAs && allPAs) {
    etkPAs.forEach((pa) => {
      const addr = new URL(pa).pathname.split("/")[3];
      paInEtk.push({
        address: addr,
        name: allPAs[addr],
      });
    });
  }
  return paInEtk;
}

export function checkVisibility(obj: any, mode: string): boolean {
  return !!obj?.custom_data && !(obj.custom_data.tags && obj.custom_data.tags.includes("hidden") && mode === "hide");
}

export function isJrOfSrFilter(etk: { sr_etks?: string[] }, srEtkFilter: string[], state: any, mode: string): boolean {
  return (
    etk.sr_etks?.some((srEtk) => {
      const addr = new URL(srEtk).pathname.split("/")[3];
      return srEtkFilter.includes(addr) && checkVisibility(selectEtkByAddress(state, addr), mode);
    }) || false
  );
}

export function isSrPool(etk: { sr_for_pas?: any[] }): boolean {
  return (etk.sr_for_pas?.length ?? 0) > 0;
}

export interface RmParamsResponse {
  moc?: number | string | null;
  jr_coll_ratio?: number | string | null;
  sr_coll_ratio?: number | string | null;
  ensuro_fee?: number | string | null;
  jr_roc?: number | string | null;
  sr_roc?: number | string | null;
  max_payout?: number | string | null;
  max_duration?: number | string | null;
  avg_payout_per_policy?: number | string | null;
  avg_policy_duration?: number | string | null;
  active_exposure?: number | string | null;
}

export function getRiskParams(params: AsyncState<RmParamsResponse> | null | undefined): RiskParams {
  const moc = params?.value
    ? { value: params.value.moc, state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const jrCollRatio = params?.value
    ? { value: params.value.jr_coll_ratio, state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const srCollRatio = params?.value
    ? { value: params.value.sr_coll_ratio, state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const ensuroFee = params?.value
    ? { value: params.value.ensuro_fee, state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const jrRoc = params?.value
    ? { value: params.value.jr_roc, state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const srRoc = params?.value
    ? { value: params.value.sr_roc, state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const maxPayout = params?.value
    ? { value: params.value.max_payout, state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const maxDuration = params?.value
    ? (() => {
        const raw = params.value.max_duration;
        const days = raw != null ? convertToDays(Number(raw)) : 0;
        return { value: Number.isFinite(days) ? days : 0, state: "LOADED" as const };
      })()
    : { state: "LOADING" as const, value: undefined };
  const avgPayoutPerPolicy = params?.value
    ? { value: params.value.avg_payout_per_policy, state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const avgPolicyDuration = params?.value
    ? (() => {
        const raw = params.value.avg_policy_duration;
        const days = raw != null ? convertToDays(Number(raw)) : 0;
        return { value: Number.isFinite(days) ? days : 0, state: "LOADED" as const };
      })()
    : { state: "LOADING" as const, value: undefined };
  const activeExposure = params?.value
    ? { value: params.value.active_exposure, state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };

  return {
    moc,
    jrCollRatio,
    srCollRatio,
    ensuroFee,
    jrRoc,
    srRoc,
    maxPayout,
    maxDuration,
    avgPayoutPerPolicy,
    avgPolicyDuration,
    activeExposure,
  };
}

export function exposureState(exposure: { value?: { active?: unknown; limit?: unknown } } | null | undefined): {
  current: LoadedState<unknown> | LoadingState;
  limit: LoadedState<unknown> | LoadingState;
} {
  const value = exposure?.value;
  const loaded = value != null;
  return {
    current: loaded
      ? { value: value.active, state: "LOADED" as const }
      : { state: "LOADING" as const, value: undefined },
    limit: loaded ? { value: value.limit, state: "LOADED" as const } : { state: "LOADING" as const, value: undefined },
  };
}

export function getEtkWithURL(
  reducer: any,
  url: string | null | undefined
): AsyncState<EtkWithName> | Record<string, never> {
  if (!url) return {};
  const addr = new URL(url).pathname.split("/")[3];
  if (!addr) return {};
  const etk = selectEtkByAddress(reducer, addr);
  return etk ? { state: "LOADED" as const, value: { name: etk.name, address: etk.address } } : {};
}

export function getPAWithURL(
  reducer: any,
  url: string | null | undefined
): AsyncState<EtkWithName> | Record<string, never> {
  if (!url) return {};
  const addr = new URL(url).pathname.split("/")[3];
  if (!addr) return {};
  const pa = selectPAByAddress(reducer, addr);
  return pa ? { state: "LOADED" as const, value: { name: pa.name, address: pa.address } } : {};
}
